/**
 * Host-side durable-memory extraction (ENG-6200).
 *
 * Memory "dreaming" (packages/api/src/cron/memory-dreaming.ts) used to
 * consolidate durable agent_memories from the `sessions` table. That table
 * stopped being written for the Claude Code fleet at the persistent-session
 * cutover (~2026-05-09), so dreaming has promoted nothing fleet-wide since — and
 * even when it worked it was content-blind, fed only per-session tool-id lists.
 *
 * This is the replacement: for each completed end-user conversation the control
 * plane flags as pending, reconstruct the REAL transcript from the agent's local
 * session files, extract candidate memories with a small model, and POST only
 * the candidates back (POST /host/memories/candidates). The API runs the shared
 * promote gate (dedupe / threshold / embed / dream_log).
 *
 * SECURITY / DATA-MINIMISATION (the whole point of doing this host-side):
 *   The raw transcript NEVER leaves the host — identical boundary to
 *   conversation-evaluator.ts. The control plane hands us only
 *   conversation_id + channel + channel_ref (no content); the manager reads the
 *   content from on-disk transcripts, extracts locally via the agent's OWN auth
 *   (so content only reaches the same backend the evaluator already uses), and
 *   POSTs back nothing but {name, content, type, confidence, rationale}
 *   candidates — the same low-sensitivity shape the dreaming cron produced.
 *
 * Same fire-and-forget contract as conversation-evaluator.ts: self-throttled,
 * self-bounded, errors swallowed — must never slow or break the manager poll
 * cycle. Claude Code framework only. Reuses the evaluator's pluggable backend
 * (claude-p / local) and its transcript-reconstruction helpers.
 */

import {
  reconstructConversation,
  renderTranscript,
  readRecentTurns,
  type ConversationEvalBackend,
  type ConversationEvaluatorApi,
} from './conversation-evaluator.js';
import { sessionTranscriptDir } from './daily-session.js';
import { getProjectDir } from './persistent-session.js';

// Memories are even lower-value-per-poll than evals and a completed conversation
// doesn't change, so check less often than the evaluator's 5 min.
const MIN_CHECK_INTERVAL_MS = 10 * 60_000;
// Pad the [started_at, last_message_at] window when selecting turns so a
// slightly skewed transcript timestamp still lands inside (mirrors the evaluator).
const WINDOW_PAD_MS = 5 * 60_000;

const VALID_MEMORY_TYPES = new Set(['user', 'feedback', 'project', 'reference']);

interface PendingConversation {
  conversation_id: string;
  channel: string;
  channel_ref: string;
  started_at: string;
  last_message_at: string;
  message_count: number;
}

/**
 * The low-sensitivity candidate shape POSTed to the control plane — identical to
 * packages/api/src/lib/dream-promote.ts's CandidateMemory. The API re-validates
 * via normalizeCandidates, so this is best-effort.
 */
export interface ExtractedCandidate {
  name: string;
  content: string;
  type: 'user' | 'feedback' | 'project' | 'reference';
  confidence: number;
  rationale: string;
}

// Defense-in-depth host-side scrub. The transcript is untrusted user content,
// and a prompt-injected conversation could coax the model into echoing a secret
// into a candidate's content/rationale — which would then leave the host in the
// POST body, breaking the on-host privacy boundary. The prompt asks the model
// not to, but we redact obvious secret SHAPES before anything crosses the wire.
// This is not a full DLP pass — it targets the high-signal exfil shapes (keys,
// tokens, long hex/base64 blobs, emails). The shared promote gate stores
// whatever survives.
const SECRET_PATTERNS: RegExp[] = [
  // labelled secrets: `api_key=...`, `password: ...`, `bearer ...`
  /\b[\w-]*(?:secret|password|passwd|token|api[_-]?key|bearer)[\w-]*\s*[=:]\s*\S+/gi,
  // common credential prefixes (stripe, github, slack, augmented host keys, …)
  /\b(?:sk|pk|rk|tlk|ghp|gho|ghs|xox[baprs])[-_][A-Za-z0-9]{12,}\b/g,
  // long hex blobs (hashes / hex-encoded keys)
  /\b[A-Fa-f0-9]{32,}\b/g,
  // long base64 blobs (tokens / encoded secrets)
  /\b[A-Za-z0-9+/]{40,}={0,2}\b/g,
  // email addresses
  /\b[\w.+-]+@[\w-]+\.[\w.-]+\b/g,
];

/** Redact obvious secret shapes from a candidate field before it leaves the host. */
export function scrubSensitive(text: string): string {
  let out = text;
  for (const re of SECRET_PATTERNS) out = out.replace(re, '[redacted]');
  return out;
}

interface ExtractorState {
  lastCheckedAt: number;
}

const state = new Map<string, ExtractorState>();

/** Test helper — reset module state. Not for production use. */
export function _resetMemoryExtractorState(): void {
  state.clear();
}

/**
 * Build the extraction prompt. Unlike the dreaming cron (which only ever saw
 * tool-id lists), this feeds the model the REAL User/Agent turns, so it can
 * extract content-based memories ("Andy prefers 5-frame carousels", "delivered
 * the Q3 deck for Two Tractors").
 */
export function buildExtractionPrompt(channel: string, transcript: string): string {
  return `You are extracting durable, reusable memories from one completed ${channel} conversation between an AI agent and an end-user. A good memory is a stable preference, fact, correction, or recurring work context that will help the agent in FUTURE conversations — not a one-off task detail or pleasantry.

Conversation transcript:
"""
${transcript}
"""

Extract 0-5 candidate memories. For each:
- name: short descriptive title (max 50 chars)
- content: the memory itself, self-contained (max 200 chars). Do NOT include secrets or sensitive personal data.
- type: "user" (a person's stable preference), "feedback" (a correction to how the agent should work), "project" (durable work context), or "reference" (a pointer to an external resource)
- confidence: 0.0-1.0 — how sure you are this is worth remembering long-term
- rationale: why this should persist (max 100 chars)

If there is nothing durable worth remembering, return an empty array. Do NOT invent memories to fill the list.

Respond with ONLY a JSON array, no other text:
[{"name":"...","content":"...","type":"...","confidence":0.8,"rationale":"..."}]`;
}

/**
 * Parse the model's JSON array of candidates. Tolerant of markdown fences and
 * surrounding prose.
 *
 * Returns `null` when the output contains NO parseable JSON array (no/bad JSON,
 * prose-only garbage) — a transient model hiccup the caller must RETRY, never
 * finalise. Returns an array (possibly empty) when the model produced a valid
 * array: an empty one is a legitimate "nothing durable here" that finalises the
 * conversation. This mirrors conversation-evaluator's parseVerdict→null contract.
 */
export function parseCandidates(raw: string): ExtractedCandidate[] | null {
  const match = raw.match(/\[[\s\S]*\]/);
  if (!match) return null;
  let arr: unknown;
  try {
    arr = JSON.parse(match[0]);
  } catch {
    return null;
  }
  if (!Array.isArray(arr)) return null;
  const out: ExtractedCandidate[] = [];
  for (const entry of arr) {
    if (!entry || typeof entry !== 'object') continue;
    const c = entry as Partial<ExtractedCandidate>;
    const name = typeof c.name === 'string' ? c.name.trim() : '';
    if (!name) continue;
    if (typeof c.type !== 'string' || !VALID_MEMORY_TYPES.has(c.type)) continue;
    const confidence =
      typeof c.confidence === 'number' && Number.isFinite(c.confidence)
        ? Math.max(0, Math.min(1, c.confidence))
        : 0;
    if (confidence === 0) continue;
    out.push({
      name: name.slice(0, 80),
      content: (typeof c.content === 'string' ? c.content : '').trim().slice(0, 500),
      type: c.type as ExtractedCandidate['type'],
      confidence,
      rationale: (typeof c.rationale === 'string' ? c.rationale : '').trim().slice(0, 200),
    });
  }
  return out;
}

/**
 * For one agent: pull pending conversations, reconstruct each from local
 * transcripts, extract candidate memories, and POST them. Self-throttled and
 * bounded; all faults are swallowed (Claude Code only).
 */
export async function maybeExtractMemories(args: {
  api: ConversationEvaluatorApi;
  backend: ConversationEvalBackend;
  codeName: string;
  agentId: string;
  log: (msg: string) => void;
  now?: Date;
  /** Test seam: override the transcript directory. */
  transcriptDir?: string;
}): Promise<void> {
  const { api, backend, codeName, agentId, log } = args;
  const now = args.now ?? new Date();
  const nowMs = now.getTime();

  const existing = state.get(codeName);
  if (existing && nowMs - existing.lastCheckedAt < MIN_CHECK_INTERVAL_MS) {
    return;
  }
  state.set(codeName, { lastCheckedAt: nowMs });

  let pending: PendingConversation[];
  try {
    const resp = await api.get<{ ok?: boolean; conversations?: PendingConversation[] }>(
      `/host/memories/pending-extraction?agent_id=${encodeURIComponent(agentId)}`,
    );
    pending = resp?.conversations ?? [];
  } catch (err) {
    log(`[memory-extract] ${codeName}: pending fetch failed: ${(err as Error).message}`);
    return;
  }
  if (pending.length === 0) return;

  // Read recent transcripts once, then mine each conversation against them.
  const dir = args.transcriptDir ?? sessionTranscriptDir(getProjectDir(codeName));
  const allTurns = readRecentTurns(dir, nowMs);
  if (allTurns.length === 0) {
    // No local transcript content at all — bump attempts so we don't spin.
    for (const conv of pending) {
      await reportSkip(api, agentId, conv.conversation_id, log, codeName);
    }
    return;
  }

  for (const conv of pending) {
    const windowStart = Date.parse(conv.started_at) - WINDOW_PAD_MS;
    const windowEnd = Date.parse(conv.last_message_at) + WINDOW_PAD_MS;
    // ENG-7137: reconstructConversation now derives both the user-match and
    // reply-routing tokens from the channel_ref itself (and credits routed
    // agent replies by id), so memory extraction gets the same higher-fidelity
    // transcript the conversation evaluator does.
    const turns = reconstructConversation(allTurns, conv.channel_ref, windowStart, windowEnd);
    if (turns.length === 0) {
      await reportSkip(api, agentId, conv.conversation_id, log, codeName);
      continue;
    }

    const transcript = renderTranscript(turns);
    if (!transcript.trim()) {
      await reportSkip(api, agentId, conv.conversation_id, log, codeName);
      continue;
    }

    // Extraction failures are transient (endpoint down, model hiccup, garbled
    // output): log and retry on a later cycle WITHOUT reportSkip — the attempts
    // budget is for permanently-unreconstructable conversations, and a
    // reconstructable one mustn't be permanently suppressed by a few hiccups.
    // A `null` parse (no valid JSON array) is the same transient class and must
    // NOT be POSTed — an empty POST would finalise the conversation and suppress
    // retries. Only a genuine array (possibly empty) is reported.
    let candidates: ExtractedCandidate[] | null;
    try {
      const out = await backend.run(buildExtractionPrompt(conv.channel, transcript));
      candidates = parseCandidates(out);
    } catch (err) {
      log(`[memory-extract] ${codeName}: extraction failed: ${(err as Error).message}`);
      continue;
    }
    if (candidates === null) {
      log(`[memory-extract] ${codeName}: unparseable output for ${conv.conversation_id.slice(0, 8)} — will retry`);
      continue;
    }

    // Host-side scrub: redact obvious secret shapes the model may have echoed
    // from an injected transcript before the candidates leave the box.
    const scrubbed = candidates.map((c) => ({
      ...c,
      content: scrubSensitive(c.content),
      rationale: scrubSensitive(c.rationale),
    }));

    try {
      const res = await api.post<{ ok?: boolean; promoted?: number; logged?: number }>(
        '/host/memories/candidates',
        {
          agent_id: agentId,
          conversation_id: conv.conversation_id,
          candidates: scrubbed,
        },
      );
      log(
        `[memory-extract] ${codeName}: ${conv.conversation_id.slice(0, 8)} → ${candidates.length} candidate(s), ${res?.promoted ?? 0} promoted`,
      );
    } catch (err) {
      log(`[memory-extract] ${codeName}: report failed: ${(err as Error).message}`);
    }
  }
}

/** Report a conversation we couldn't reconstruct, bumping its retry counter. */
async function reportSkip(
  api: ConversationEvaluatorApi,
  agentId: string,
  conversationId: string,
  log: (msg: string) => void,
  codeName: string,
): Promise<void> {
  try {
    await api.post('/host/memories/candidates', {
      agent_id: agentId,
      conversation_id: conversationId,
      skipped: true,
    });
  } catch (err) {
    log(`[memory-extract] ${codeName}: skip report failed: ${(err as Error).message}`);
  }
}
